import { jwtDecode } from "jwt-decode"
import { getRequest } from "../services"
import {
  sendEmailNotification,
  sendNotification,
} from "../redux/notificationsSlice"

const MANAGEMENT_CACHE_TTL_MS = 60_000
let managementUsersCache = null
let managementUsersCacheTime = 0

const looksLikeEmail = (value) =>
  typeof value === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())

const firstNonEmpty = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim()
    }
  }
  return ""
}

const escapeHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")

export const getLoggedInLmsUser = () => {
  try {
    const auth = JSON.parse(localStorage.getItem("user") || "{}")
    const decoded = auth.token ? jwtDecode(auth.token) : {}

    return {
      userId: firstNonEmpty(
        auth.userId,
        decoded.id,
        decoded.userId,
        decoded.nameid,
        decoded.sub,
        decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"]
      ),
      email: firstNonEmpty(
        looksLikeEmail(auth.email) ? auth.email : "",
        looksLikeEmail(decoded.email) ? decoded.email : "",
        looksLikeEmail(decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"])
          ? decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"]
          : "",
        looksLikeEmail(decoded.unique_name) ? decoded.unique_name : ""
      ),
    }
  } catch {
    return { userId: "", email: "" }
  }
}

const fetchManagementUsers = async () => {
  const now = Date.now()
  if (managementUsersCache && now - managementUsersCacheTime < MANAGEMENT_CACHE_TTL_MS) {
    return managementUsersCache
  }

  const response = await getRequest("/api/Management")
  managementUsersCache = Array.isArray(response.data) ? response.data : []
  managementUsersCacheTime = now
  return managementUsersCache
}

const resolveUserIdByEmail = async (email) => {
  if (!email) return null

  try {
    const users = await fetchManagementUsers()
    const normalized = email.trim().toLowerCase()
    const match = users.find(
      (user) => (user.email || "").trim().toLowerCase() === normalized
    )

    return match?.id || match?.userId || null
  } catch {
    return null
  }
}

const resolveSuperAdminUserId = async ({ initiatedByUserId, initiatedByEmail }) => {
  if (initiatedByUserId) {
    return initiatedByUserId
  }

  return resolveUserIdByEmail(initiatedByEmail)
}

export const buildWiserbeeEmailHtml = ({
  heading,
  greetingName,
  message,
  buttonLabel,
  buttonUrl,
}) => {
  const year = new Date().getFullYear()
  const safeHeading = escapeHtml(heading)
  const safeName = escapeHtml(greetingName || "there")
  const safeMessage = escapeHtml(message)
  const safeButtonLabel = escapeHtml(buttonLabel)
  const safeButtonUrl = escapeHtml(buttonUrl)

  return `<!DOCTYPE html>
<html>
<head>
<style>
body { font-family: Arial, sans-serif; background-color: #f4f4f9; margin: 0; padding: 0; }
.email-container { max-width: 600px; margin: 20px auto; background: #ffffff; border: 1px solid #dddddd; border-radius: 8px; box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1); overflow: hidden; }
.email-header { background-color: #241763; color: #ffffff; text-align: center; padding: 18px 16px; font-size: 22px; font-weight: bold; }
.email-accent { height: 6px; background-color: #ED6D41; }
.email-content { padding: 24px; color: #333333; line-height: 1.6; }
.email-content p { margin: 0 0 14px; }
.cta-button { display: inline-block; margin-top: 8px; padding: 12px 20px; background-color: #ED6D41; color: #ffffff !important; text-decoration: none; border-radius: 6px; font-weight: bold; }
.email-footer { text-align: center; padding: 12px; font-size: 13px; color: #888888; background-color: #f9f9f9; }
</style>
</head>
<body>
<div class="email-container">
<div class="email-header">${safeHeading}</div>
<div class="email-accent"></div>
<div class="email-content">
<p>Hi ${safeName},</p>
<p>${safeMessage}</p>
${
  buttonUrl
    ? `<p><a class="cta-button" href="${safeButtonUrl}" target="_blank">${safeButtonLabel}</a></p>`
    : ""
}
<p>Best Regards,<br/>The Wiserbee Team</p>
</div>
<div class="email-footer">&copy; ${year} Wiserbee. All rights reserved.</div>
</div>
</body>
</html>`
}

const sendFormEmail = (dispatch, { to, subject, heading, greetingName, message, buttonLabel, buttonUrl }) => {
  if (!looksLikeEmail(to)) return

  const html = buildWiserbeeEmailHtml({
    heading,
    greetingName,
    message,
    buttonLabel,
    buttonUrl,
  })

  dispatch(
    sendEmailNotification({
      to,
      To: to,
      subject,
      Subject: subject,
      body: html,
      Body: html,
    })
  )
}

export const notifyAdminFormAction = (
  dispatch,
  admin,
  title,
  message,
  linkPath = "/account-settings"
) => {
  const adminUserId = admin?.id || admin?.userId
  const url = `${window.location.origin}${linkPath}`
  const adminName = `${admin?.firstName || ""} ${admin?.lastName || ""}`.trim()

  if (adminUserId) {
    dispatch(
      sendNotification({
        UserIds: [adminUserId],
        Title: title,
        Body: `${message} <a href="${url}" target="_blank">Open Account Settings</a>`,
      })
    )
  }

  sendFormEmail(dispatch, {
    to: admin?.email,
    subject: title,
    heading: title,
    greetingName: adminName || "there",
    message,
    buttonLabel: "Open Account Settings",
    buttonUrl: url,
  })
}

export const notifySuperAdminFormSubmission = async (
  dispatch,
  {
    initiatedByUserId,
    initiatedByEmail,
    adminName,
    title,
    message,
    linkPath = "/management",
  }
) => {
  const url = `${window.location.origin}${linkPath}`
  const byAdmin = adminName ? ` by ${adminName}` : ""
  const fullMessage = `${message}${byAdmin}.`
  const linkLabel = linkPath === "/management" ? "Open Management" : "Open"

  let superAdminEmail = looksLikeEmail(initiatedByEmail) ? initiatedByEmail.trim() : ""
  if (!superAdminEmail && initiatedByUserId) {
    try {
      const response = await getRequest(`/api/Management/${initiatedByUserId}`)
      if (looksLikeEmail(response?.data?.email)) {
        superAdminEmail = response.data.email.trim()
      }
    } catch {
      // Admins may not be able to look up Management users
    }
  }

  sendFormEmail(dispatch, {
    to: superAdminEmail,
    subject: title,
    heading: title,
    greetingName: "there",
    message: fullMessage,
    buttonLabel: linkLabel,
    buttonUrl: url,
  })

  try {
    const superAdminUserId = await resolveSuperAdminUserId({
      initiatedByUserId,
      initiatedByEmail,
    })
    if (!superAdminUserId) return

    await dispatch(
      sendNotification({
        UserIds: [superAdminUserId],
        Title: title,
        Body: `${fullMessage} <a href="${url}" target="_blank">${linkLabel}</a>`,
      })
    ).unwrap()
  } catch {
    // Notification failure should not block form submission
  }
}
